/**
 * WaitingActionsCell — what you can do with a parked element: put it back in
 * the queue line for the next rotation, or send it live right now.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { QueueElement } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { useMarkReady } from '../../../../hooks/useMarkReady';
import { IconMoveToQueue, queueActionGlyph, queueActionSurface } from '../../queue-action-style';
import MoveToLiveButton from '../../promote/MoveToLiveButton';

export interface WaitingActionsCellProps {
  element: QueueElement;
  campaignId: string;
  provider: QueueProvider;
}

const WaitingActionsCell: FC<WaitingActionsCellProps> = ({
  element,
  campaignId,
  provider,
}) => {
  const markReady = useMarkReady(campaignId, provider);
  const pending =
    markReady.isPending && markReady.variables?.bankId === element.bankId;
  return (
    <span className="inline-flex items-center justify-end gap-1">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={cn('h-7 shrink-0 gap-1 text-[11px]', queueActionSurface('queue'))}
        loading={pending}
        disabled={markReady.isPending}
        onClick={() =>
          markReady.mutate({ layer: element.layer, bankId: element.bankId })
        }
      >
        <IconMoveToQueue
          className={cn('h-3.5 w-3.5', queueActionGlyph('queue'))}
          aria-hidden
        />
        Move to queue
      </Button>
      <MoveToLiveButton element={element} busy={pending} />
    </span>
  );
};

export default WaitingActionsCell;
